import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Theme,
  Typography,
} from '@mui/material';
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

import { Spinner } from '@/components/common/Spinner';
import { ApplicationCycleContext } from '@/Contexts/ApplicationCycleContext';
import { ConfigContext } from '@/Contexts/ConfigContext';
import { LocaleContext } from '@/Contexts/LocaleContext';
import { SelectedPartnerContext } from '@/Contexts/SelectedPartnerContext';
import { SelectedProgramContext } from '@/Contexts/SelectedProgramContext';
import { SnackbarContext } from '@/Contexts/SnackbarProvider';
import { UsersContext } from '@/Contexts/UsersContext';
import useContact from '@/hooks/useContact';
import usePartner from '@/hooks/usePartner';
import { AddressProps } from '@/models/Address';
import { ApplicationTypes } from '@/models/ApplicationType';
import { ContactProps, ContactType, localizedTextEquals } from '@/models/Contact';
import { PartnerProps } from '@/models/Partner';
import { PartnerDashboardDetailsProps } from '@/models/PartnerDashboard';
import usePartnerYearsRecognized from '@/hooks/usePartnerYearsRecognized';

import { AwardsTab } from './components/AwardsTab';
import { ContactsTab } from './components/ContactsTab';
import { PartnerUpdateDialogShell } from './components/PartnerUpdateDialogShell';
import { PartnerModalTabs } from './components/PartnerModalTabs';
import { PartnerInfoTab } from './components/PartnerInfoTab';
import { PartnerModalFooterActions } from './components/PartnerModalFooterActions';
import { useAwardsTabSave } from './hooks/useAwardsTabSave';
import { useContactsTabSave } from './hooks/useContactsTabSave';
import { usePartnerInfoTabSave } from './hooks/usePartnerInfoTabSave';
import { usePartnerUpdateSubmit } from './hooks/usePartnerUpdateSubmit';
import { usePrimaryContactOptions } from './hooks/usePrimaryContactOptions';
import {
  createModalTabBooleanState,
  DEFAULT_MODAL_TAB,
  getModalTabConfig,
  ModalTab,
} from './models/modalTabs';

function mapAddressToPartnerPatch(address: AddressProps): Partial<PartnerDashboardDetailsProps> {
  return {
    shippingAddress1: address.streetAddress1,
    shippingAddress2: address.streetAddress2,
    city: address.city,
    stateId: address.stateId,
    countyId: address.countyId,
    zipCode: address.zipCode,
    countryId: address.countryId,
  };
}

export default function PartnerUpdateModal({
  partnerUpdateModalOpen,
  modalPartner,
  setPartnerUpdateModalOpen,
  theme,
  handleUpdate,
}: {
  partnerUpdateModalOpen: boolean;
  modalPartner: PartnerDashboardDetailsProps;
  setPartnerUpdateModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  theme: Theme;
  handleUpdate: (partnerDetails: PartnerDashboardDetailsProps) => void;
}) {
  const { setAlert } = useContext(SnackbarContext);
  const { translatedText } = useContext(LocaleContext);
  const { applicationCycles } = useContext(ApplicationCycleContext);
  const { selectedProgramId } = useContext(SelectedProgramContext);
  const { userType } = useContext(UsersContext);
  const { contactTypes } = useContext(ConfigContext);
  const { partnersAll, updatePartnersAll } = useContext(SelectedPartnerContext);

  const { getPartnersByPartnerGroupId, patchPartnerRecord } = usePartner();
  const { saveContact, getAllContactsByPartnerId } = useContact();
  const { postPartnerYearsRecognized } = usePartnerYearsRecognized();

  const [partnerInfoLocal, setPartnerInfoLocal] =
    useState<PartnerDashboardDetailsProps>(modalPartner);
  const patchPartner = useCallback((patch: Partial<PartnerDashboardDetailsProps>) => {
    setPartnerInfoLocal((prev) => ({ ...prev, ...patch }));
  }, []);

  // Local-only UI state
  const [shippingContact, setShippingContact] = useState<ContactProps>({});
  const [selectedContactId, setSelectedContactId] = useState<number | null>(null);
  const [showAlert, setShowAlert] = useState<boolean>(false);
  const [activeTab, setActiveTab] = useState<ModalTab>(DEFAULT_MODAL_TAB);
  const [dirtyTabs, setDirtyTabs] = useState<Record<ModalTab, boolean>>(
    createModalTabBooleanState(false)
  );
  const [savedTabs, setSavedTabs] = useState<Record<ModalTab, boolean>>(
    createModalTabBooleanState(false)
  );
  const [errorTabs, setErrorTabs] = useState<Record<ModalTab, boolean>>(
    createModalTabBooleanState(false)
  );
  const [savingTab, setSavingTab] = useState<ModalTab | null>(null);
  const [savingAwards, setSavingAwards] = useState(false);
  const [closeConfirmOpen, setCloseConfirmOpen] = useState(false);
  const wasOpenRef = useRef(false);
  const initializedPartnerIdRef = useRef<number | null | undefined>(undefined);

  const modalTabConfig = useMemo(() => getModalTabConfig(translatedText), [translatedText]);

  const markTabDirty = useCallback((tab: ModalTab) => {
    setDirtyTabs((prev) => ({ ...prev, [tab]: true }));
    setSavedTabs((prev) => ({ ...prev, [tab]: false }));
    setErrorTabs((prev) => ({ ...prev, [tab]: false }));
  }, []);

  const markTabClean = useCallback((tab: ModalTab) => {
    setDirtyTabs((prev) => ({ ...prev, [tab]: false }));
  }, []);

  const markTabSaved = useCallback((tab: ModalTab) => {
    setSavedTabs((prev) => ({ ...prev, [tab]: true }));
    setErrorTabs((prev) => ({ ...prev, [tab]: false }));
  }, []);

  const markTabError = useCallback((tab: ModalTab) => {
    setErrorTabs((prev) => ({ ...prev, [tab]: true }));
    setSavedTabs((prev) => ({ ...prev, [tab]: false }));
  }, []);

  const closeModal = useCallback(() => {
    setPartnerUpdateModalOpen(false);
  }, [setPartnerUpdateModalOpen]);

  const hasAnyDirtyTab = useMemo(() => Object.values(dirtyTabs).some(Boolean), [dirtyTabs]);

  const handleClose = useCallback(() => {
    if (hasAnyDirtyTab) {
      setCloseConfirmOpen(true);
      return;
    }

    closeModal();
  }, [closeModal, hasAnyDirtyTab]);

  const handleDiscardAndClose = useCallback(() => {
    setCloseConfirmOpen(false);
    closeModal();
  }, [closeModal]);

  const handleKeepEditing = useCallback(() => {
    setCloseConfirmOpen(false);
  }, []);

  useEffect(() => {
    if (!partnerUpdateModalOpen) {
      wasOpenRef.current = false;
      return;
    }

    const isOpening = !wasOpenRef.current;
    const partnerId = modalPartner?.id;
    const partnerChanged = initializedPartnerIdRef.current !== partnerId;

    if (!isOpening && !partnerChanged) return;

    setPartnerInfoLocal(modalPartner);
    setShowAlert(false);
    setActiveTab(DEFAULT_MODAL_TAB);
    setDirtyTabs(createModalTabBooleanState(false));
    setSavedTabs(createModalTabBooleanState(false));
    setErrorTabs(createModalTabBooleanState(false));
    setCloseConfirmOpen(false);

    if (modalPartner?.shippingContact != null) setShippingContact(modalPartner.shippingContact);
    else setShippingContact({});

    if (modalPartner?.primaryContact?.id != null)
      setSelectedContactId(modalPartner.primaryContact.id);
    else setSelectedContactId(null);

    initializedPartnerIdRef.current = partnerId;
    wasOpenRef.current = true;
  }, [modalPartner, partnerUpdateModalOpen]);

  const shippingContactType = useMemo(
    () =>
      contactTypes.find(
        (c: ContactType) =>
          localizedTextEquals(c.contactTypeName, 'Shipping Address') &&
          c.programId === selectedProgramId
      ),
    [contactTypes, selectedProgramId]
  );

  const { options: contactOptions, loading: contactsLoading } = usePrimaryContactOptions({
    partnerGroupId: modalPartner?.partnerGroup?.id,
    primaryContactId: modalPartner?.primaryContactId,
    getPartnersByPartnerGroupId,
    getAllContactsByPartnerId,
  });

  const updatePartners = useCallback(
    (updatedPartner: PartnerProps) => {
      const allPartners = { ...partnersAll };
      const programPartners = [...(allPartners[selectedProgramId] ?? [])];

      const idx = programPartners.map((x) => x.id).indexOf(updatedPartner.id);
      if (idx >= 0 && programPartners[idx] != null) {
        programPartners[idx] = { ...programPartners[idx], ...updatedPartner };
        allPartners[selectedProgramId] = programPartners;
        updatePartnersAll(allPartners);
      }
    },
    [partnersAll, selectedProgramId, updatePartnersAll]
  );

  const commitUpdatedPartner = useCallback(
    (partner: PartnerProps) => {
      // Keep the existing behavior: compute yearsAwarded/latestApprovalYear and push updates upward.
      let updatedPartner: PartnerDashboardDetailsProps = {
        ...partnerInfoLocal,
        partnerYearsRecognized: partner.partnerYearsRecognized,
        yearsAwarded:
          partner.partnerYearsRecognized != null
            ? partner.partnerYearsRecognized.filter(
                (pyr) =>
                  pyr.programId === selectedProgramId &&
                  pyr.applicationTypeId === ApplicationTypes.Standard &&
                  pyr.partnerId === modalPartner.id
              ).length
            : 0,
        growthAwardYearsAwarded:
          partner.partnerYearsRecognized != null
            ? partner.partnerYearsRecognized.filter(
                (pyr) =>
                  pyr.programId === selectedProgramId &&
                  pyr.applicationTypeId === ApplicationTypes.GrowthAward &&
                  pyr.partnerId === modalPartner.id
              ).length
            : 0,
      };

      if (partner.partnerYearsRecognized != null) {
        const ids = partner.partnerYearsRecognized.map((pyr) => pyr.applicationCycleId);

        const latestYearAwarded = applicationCycles
          .filter((ac) => ids.includes(ac.id))
          .reduce((max, ac) => (ac.year > max ? ac.year : max), 0);

        updatedPartner = {
          ...updatedPartner,
          latestApprovalYear: latestYearAwarded,
        };
      }

      // Push changes up + into partner list
      handleUpdate(updatedPartner);
      updatePartners(updatedPartner);
      setPartnerInfoLocal(updatedPartner);
    },
    [
      applicationCycles,
      handleUpdate,
      modalPartner.id,
      partnerInfoLocal,
      selectedProgramId,
      updatePartners,
    ]
  );

  const handlePartnerYearsRecognizedDraftChange = useCallback(
    (partner: PartnerProps) => {
      setPartnerInfoLocal((prev) => ({
        ...prev,
        partnerYearsRecognized: partner.partnerYearsRecognized,
      }));
      markTabDirty('awards');
    },
    [markTabDirty]
  );

  const handlePartnerAddressUpdate = useCallback(
    (address: AddressProps) => {
      patchPartner(mapAddressToPartnerPatch(address));
      setShippingContact((prev) => ({ ...prev, ...address }));
    },
    [patchPartner]
  );

  const { posting, submit } = usePartnerUpdateSubmit({
    shippingContactType,
    saveContact,
    patchPartnerRecord,
    setAlert,
  });

  const isPartnerNameMissing = (partnerInfoLocal.partnerName ?? '').trim().length === 0;
  const isCurrentTabDirty = dirtyTabs[activeTab];
  const isSaving = posting || savingAwards;

  const onUpdatedPartner = useCallback(
    (updated: PartnerProps) => {
      setPartnerInfoLocal(updated);
      handleUpdate(updated);
      updatePartners(updated);
    },
    [handleUpdate, updatePartners]
  );

  const savePartnerTab = usePartnerInfoTabSave({
    submit,
    partnerInfoLocal,
    shippingContact,
    isPartnerNameMissing,
    setShowAlert,
    setAlert,
    setSavingTab,
    onUpdatedPartner,
    markTabClean,
    markTabSaved,
    markTabError,
  });

  const saveContactsTab = useContactsTabSave({
    submit,
    partnerInfoLocal,
    shippingContact,
    setShowAlert,
    setSavingTab,
    onUpdatedPartner,
    markTabClean,
    markTabSaved,
    markTabError,
  });

  const saveAwardsTab = useAwardsTabSave({
    partnerInfoLocal,
    selectedProgramId,
    postPartnerYearsRecognized,
    setAlert,
    setSavingTab,
    setSavingAwards,
    commitUpdatedPartner,
    markTabClean,
    markTabSaved,
    markTabError,
  });

  const validationErrorTabs: Partial<Record<ModalTab, boolean>> = {
    partner: isPartnerNameMissing,
  };

  const isModalReady = modalPartner?.partnerGroup?.id != null;
  const tabFormSx = {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
  };

  const sectionSx = {
    border: '1px solid',
    borderColor: 'divider',
    gap: 2,
    borderRadius: 2,
    p: 2,
    backgroundColor: theme.palette.mode === 'light' ? 'grey.50' : 'background.default',
  };

  const sectionTitleSx = {
    fontWeight: 600,
    mb: 2,
  };

  const tabRegistry = {
    partner: {
      save: savePartnerTab,
      panel: (
        <PartnerInfoTab
          active={activeTab === 'partner'}
          partnerInfo={partnerInfoLocal}
          isPartnerNameMissing={isPartnerNameMissing}
          userType={userType}
          showErrors={showAlert}
          labelCountry={translatedText.address.labelCountry}
          labelState={translatedText.address.labelState}
          labelCounty={translatedText.address.labelCounty}
          tabFormSx={tabFormSx}
          sectionSx={sectionSx}
          sectionTitleSx={sectionTitleSx}
          onPatchPartner={patchPartner}
          onMarkDirty={() => markTabDirty('partner')}
        />
      ),
    },
    contacts: {
      save: saveContactsTab,
      panel: (
        <ContactsTab
          active={activeTab === 'contacts'}
          theme={theme}
          userType={userType}
          selectedContactId={selectedContactId}
          contactOptions={contactOptions}
          contactsLoading={contactsLoading}
          shippingContact={shippingContact}
          shippingContactType={shippingContactType}
          showErrors={showAlert}
          shippingHeader={translatedText.signup.headerShipping}
          tabFormSx={tabFormSx}
          sectionSx={sectionSx}
          sectionTitleSx={sectionTitleSx}
          programId={partnerInfoLocal.programId}
          onSelectPrimaryContact={(id, contact) => {
            setSelectedContactId(id);
            patchPartner({
              primaryContactId: id,
              primaryContact: contact,
            });
          }}
          onShippingAddressChange={handlePartnerAddressUpdate}
          onMarkDirty={() => markTabDirty('contacts')}
        />
      ),
    },
    awards: {
      save: saveAwardsTab,
      panel: (
        <AwardsTab
          active={activeTab === 'awards'}
          partnerInfo={partnerInfoLocal}
          selectedProgramId={selectedProgramId}
          tabFormSx={tabFormSx}
          sectionSx={sectionSx}
          sectionTitleSx={sectionTitleSx}
          onDraftPartnerYearsRecognizedChange={handlePartnerYearsRecognizedDraftChange}
        />
      ),
    },
  };

  const activeTabConfig = modalTabConfig.find((tab) => tab.id === activeTab);

  return (
    <PartnerUpdateDialogShell
      open={partnerUpdateModalOpen}
      title={translatedText.partnerUpdateModal.titleEditPartnerDetails}
      onClose={handleClose}
      fixedHeader={
        isModalReady ? (
          <PartnerModalTabs
            activeTab={activeTab}
            onChange={setActiveTab}
            dirtyTabs={dirtyTabs}
            savedTabs={savedTabs}
            errorTabs={errorTabs}
            validationErrorTabs={validationErrorTabs}
            savingTab={savingTab}
          />
        ) : null
      }
      actions={
        !isModalReady ? null : (
          <PartnerModalFooterActions
            posting={isSaving}
            theme={theme}
            saveLabel={activeTabConfig?.saveLabel}
            disableSave={!isCurrentTabDirty}
            onCancel={handleClose}
            onSave={() => {
              if (!isCurrentTabDirty) return;
              return tabRegistry[activeTab].save();
            }}
          />
        )
      }
    >
      {!isModalReady ? (
        <Spinner />
      ) : (
        <>
          {modalTabConfig.map((tab) => (
            <React.Fragment key={tab.id}>{tabRegistry[tab.id].panel}</React.Fragment>
          ))}
        </>
      )}

      <Dialog open={closeConfirmOpen} onClose={handleKeepEditing} maxWidth='xs' fullWidth>
        <DialogTitle>{translatedText.myaccount.unsavedChangesTitle}</DialogTitle>
        <DialogContent>
          <Typography>{translatedText.myaccount.unsavedChangesBody}</Typography>
        </DialogContent>
        <DialogActions>
          <Button variant='secondary' size='large' onClick={handleKeepEditing}>
            {translatedText.myaccount.buttonKeepEditing}
          </Button>
          <Button variant='primary' size='large' onClick={handleDiscardAndClose}>
            {translatedText.myaccount.buttonDiscardChanges}
          </Button>
        </DialogActions>
      </Dialog>
    </PartnerUpdateDialogShell>
  );
}
